// Printed reference ranges are not always "low – high". Israeli and Russian
// reports routinely print a one-sided threshold (">15 mil/ml", "> 40%",
// "до 5", "≤ 1"), and some report types are almost entirely one-sided — a
// spermogram, for example, prints ">15 mil/ml" for concentration, ">30 mil"
// for the total count and "> 40%" for motility, with only pH and volume as
// two-sided ranges.
//
// Two consequences this module handles:
//
// 1. The extractor returns such a range in `referenceText` and leaves
//    referenceLow/referenceHigh empty. Reading the number out of that text is
//    transcription of what is printed on THIS document — never a
//    general-knowledge range (docs/AI_RULES.md rule 1) — and the result is
//    still only a *suggestion* pre-filled into the review form, which the user
//    confirms or corrects before anything is saved (rule 2). Anything
//    ambiguous (two ranges on one line, e.g. Kruger's
//    "0-3% Poor | 4-13% Good") is deliberately left unparsed rather than
//    guessed.
// 2. A one-sided range has no second bound to draw a bar or a chart band
//    between, so the geometry below builds a display window around the value
//    and the single printed threshold instead of a low→high track.

export interface ParsedReference {
  low?: number;
  high?: number;
}

// Unit tokens that carry digits ("10^9/L", "×10⁶/ml", "10*3/uL") would
// otherwise be counted as reference numbers and make an unambiguous range
// look ambiguous. They are removed before any number is read.
const POWER_UNIT_PATTERN = /(?:[x×*]\s*)?10\s*(?:\^|\*\*|\*)\s*\d+|10[²³¹⁰⁴-⁹]+/gi;
const NUMBER_PATTERN = /\d+(?:\.\d+)?|\.\d+/g;
const RANGE_PATTERN = /(\d+(?:\.\d+)?|\.\d+)\s*(?:-|to|до)\s*(\d+(?:\.\d+)?|\.\d+)/;
const MIN_PATTERN = /(?:>=|>|от|min|не менее|не ниже|more than|above|over|at least)\s*(?:\d|\.)/;
const MAX_PATTERN = /(?:<=|<|до|max|не более|не выше|less than|below|under|up to)\s*(?:\d|\.)/;

function normalizeReference(raw: string): string {
  return raw
    // Dashes: figure/en/em/horizontal bar and the minus sign all appear as
    // range separators on scanned reports.
    .replace(/[‐-―−]/g, '-')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    // Decimal comma, but only between digits ("1,5" → "1.5"), never a list
    // separator.
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(POWER_UNIT_PATTERN, ' ')
    .toLowerCase()
    .trim();
}

// Returns null whenever the printed text does not resolve to exactly one
// unambiguous bound or range — "Normal", "Kruger 0-3% Poor | 4-13% Good",
// "см. комментарий". The caller keeps the raw text (LabResult.refText) and
// simply shows no numeric norm, which is the honest outcome.
export function parseReferenceText(raw: string | undefined): ParsedReference | null {
  if (!raw) return null;
  const text = normalizeReference(raw);
  const numbers = text.match(NUMBER_PATTERN) || [];

  if (numbers.length === 2) {
    const range = RANGE_PATTERN.exec(text);
    if (!range) return null;
    const first = Number(range[1]);
    const second = Number(range[2]);
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;
    // "8.7-7.0" is a transcription artifact, not a different range.
    return { low: Math.min(first, second), high: Math.max(first, second) };
  }

  if (numbers.length === 1) {
    const value = Number(numbers[0]);
    if (!Number.isFinite(value)) return null;
    // ">15" is treated as "15 and above". The strict reading (15 itself is
    // out of range) would flag a value sitting exactly on the printed
    // threshold, which no lab report means to say — and it matches the
    // inclusive comparison `inRange` already uses for two-sided ranges.
    if (MIN_PATTERN.test(text)) return { low: value };
    if (MAX_PATTERN.test(text)) return { high: value };
    return null;
  }

  return null;
}

// Trailing-zero-free rendering for bounds read off a report: 15, 7.6, 0.25.
export function formatLabNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(3)));
}

// Short human-readable form of whatever numeric norm a reading carries:
// "7–8.7", "≥ 15", "≤ 1". Null when nothing numeric is printed.
export function referenceCondition(refLow?: number, refHigh?: number): string | null {
  if (refLow !== undefined && refHigh !== undefined) return `${formatLabNumber(refLow)}–${formatLabNumber(refHigh)}`;
  if (refLow !== undefined) return `≥ ${formatLabNumber(refLow)}`;
  if (refHigh !== undefined) return `≤ ${formatLabNumber(refHigh)}`;
  return null;
}

export interface RangeGeometry {
  kind: 'range' | 'min' | 'max';
  // All percentages are 0..100 of the rendered track width.
  markerPercent: number;
  bandStartPercent: number;
  bandEndPercent: number;
  // Two-sided ranges label both track ends with the printed bounds. One-sided
  // ones have no honest second end to label, so they label the threshold
  // itself where it sits on the track.
  startLabel?: string;
  endLabel?: string;
  thresholdLabel?: string;
  thresholdPercent?: number;
}

function percent(value: number, min: number, max: number): number {
  const span = max - min;
  if (!(span > 0)) return 50;
  return Math.min(100, Math.max(0, ((value - min) / span) * 100));
}

// For a one-sided norm the track is a window around the value and the printed
// threshold — wide enough that both are visible and neither sits on the edge.
// The window is a drawing decision only: no number from it is ever shown as if
// it were printed on the report.
function oneSidedWindow(value: number, threshold: number): { min: number; max: number } {
  const spread = Math.abs(value - threshold);
  const pad = spread > 0 ? spread * 0.8 : Math.max(Math.abs(threshold) * 0.25, 1);
  return { min: Math.min(value, threshold) - pad, max: Math.max(value, threshold) + pad };
}

// Null when the reading has no numeric norm at all — the caller renders no bar
// rather than an empty or invented one.
export function rangeGeometry(value: number, refLow?: number, refHigh?: number): RangeGeometry | null {
  if (refLow !== undefined && refHigh !== undefined) {
    return {
      kind: 'range',
      markerPercent: percent(value, refLow, refHigh),
      bandStartPercent: 0,
      bandEndPercent: 100,
      startLabel: formatLabNumber(refLow),
      endLabel: formatLabNumber(refHigh),
    };
  }

  if (refLow !== undefined) {
    const { min, max } = oneSidedWindow(value, refLow);
    const thresholdPercent = percent(refLow, min, max);
    return {
      kind: 'min',
      markerPercent: percent(value, min, max),
      bandStartPercent: thresholdPercent,
      bandEndPercent: 100,
      thresholdLabel: `≥ ${formatLabNumber(refLow)}`,
      thresholdPercent,
    };
  }

  if (refHigh !== undefined) {
    const { min, max } = oneSidedWindow(value, refHigh);
    const thresholdPercent = percent(refHigh, min, max);
    return {
      kind: 'max',
      markerPercent: percent(value, min, max),
      bandStartPercent: 0,
      bandEndPercent: thresholdPercent,
      thresholdLabel: `≤ ${formatLabNumber(refHigh)}`,
      thresholdPercent,
    };
  }

  return null;
}
